/**
 * Applying a redline through the proposal engine: the write side of the live
 * proposal. Two operations, chosen by the caller: replace the bookmarked
 * paragraphs with the proposal built for them, or insert the redline's
 * paragraphs after the bookmarked range. Both reconcile the tracker first, so
 * a redline whose bookmarks were lost is recovered (through the server if
 * need be) before anything is written, and both write with change tracking
 * off because the package already carries its tracked changes.
 */
import { APPLY_FAILURE_LOCATION_NOT_FOUND } from "@common/constants/appliedActions";
import { OoxmlRange } from "@common/utils/redline/ooxml-range";
import type { ProposalOutcome } from "@common/utils/redline/proposal-builder";
import { type ParagraphInsertion, paragraphIsWrittenWhereGone, redlineWritesNothingWhereItsParagraphsAreGone } from "@common/utils/redline/redline-source";
import { TextEditPlanner } from "@common/utils/redline/text-edit-planner";
import { type RedlinePayload, textsPayloadFrom } from "@common/utils/redline/wire/redline-payload";
import { type RedlinePlacement, relocatesBesideParagraph } from "@common/utils/redline/wire/redline-placement";

import { withTemporaryTrackingMode, withWordContextWrite } from "../../document";
import { ApplyFailureError, CANNOT_LOCATE_TARGET_MESSAGE, CellsNotWrittenRedlineApply, PartialRedlineApply, UserFacingError, WrittenBackRedlineApply } from "../../errors";
import { type CardCellPresence, cardCellsNotWrittenOf } from "../../redline-cells-not-written";
import { AnchorLog } from "../anchor-log";
import type { AnchoredRead, AnchorTracker } from "../anchor-tracker";
import type { AnchorTrackerRegistry } from "../anchor-tracker-registry";
import { AnchoredProposal } from "./anchored-proposal";
import { RedlineRegionDisagreementReport } from "../../redline-region-disagreement-report";
import { CellChangeWriter } from "./cell-change-writer";
import { cardReadOverCoveredSpansOfPage } from "../../redline-bulk-apply-local";
import { OfficeDocumentBookmarks } from "../office/office-document-bookmarks";
import type { RecoveryConfig } from "../recovery-config";
import { TableChangeWriter } from "./table-change-writer";
import { partsOfARedlineAcrossATable, type RedlinePartAcrossATable } from "./redline-parts-across-a-table";

export interface ProposalApplyRequest {
    bookmarkNames: string[];
    redline: RedlinePayload;
    /** What names the card in a failure report: the chat card's action id, a bulk-run card's rule ids, a live-review remedy's rule id; null when the caller has none. */
    cardId: string | null;
    /**
     * The page Apply All's fold read the card on, cut from the run's one whole-document read
     * (`TableCardEntry.pageOfTheRun`): the card is built over it instead of being reconciled
     * in Word again.
     *
     * NULL, or absent
     *   a card's own Apply, and an Apply All card with no page of the run: the card is
     *   reconciled first.
     */
    pageOfTheRun?: string | null;
}

/**
 * What one Apply on bookmarks wrote: the proposal's outcome, and every package it wrote into
 * the document, in the order written.
 *
 * WHY every package and not the outcome's one
 *   A card split at a table writes one package per body paragraph (`applyEachPartAcrossATable`);
 *   the outcome carries the last part's alone, and the table of contents is rebuilt from what
 *   was written (`updateTableOfContentsFields`), so a heading written by an earlier part counts.
 *
 * EXAMPLE
 *   parts [cells, "B. Fees to Contractor" (Heading 2), "Customer may request changes."]
 *   -> packageOoxml the last part's; writtenPackageOoxmls [cells', heading's, last part's]
 */
export interface AppliedProposalOutcome extends ProposalOutcome {
    writtenPackageOoxmls: readonly string[];
}

/** What settling the bookmarks onto freshly inserted paragraphs needs: the written range and where in it the new paragraphs sit. */
interface InsertedParagraphsSettlement {
    context: Word.RequestContext;
    tracker: AnchorTracker;
    bookmarks: OfficeDocumentBookmarks;
    written: Word.Range;
    insertedCount: number;
    placedAfterAnchor: boolean;
}

export class ProposalApplier {
    constructor(
        private readonly trackers: AnchorTrackerRegistry,
        private readonly config: RecoveryConfig,
    ) {}

    /**
     * The read a card is built over.
     *
     * CASES
     *   - Apply All hands the page its fold read the card on -> that page, over the spans the
     *                                                           card's tracker covers on it
     *                                                           (`coveredSpansOver`), with no Word read:
     *                                                           the fold gives each paragraph one entry,
     *                                                           so no earlier write of the run touched them
     *   - otherwise                                           -> the card's tracker reconciled under "apply"
     *
     * NULL
     *   the reconcile found none of the card's bookmarks, and recovery none of its paragraphs.
     */
    private static async anchoredReadFor(request: ProposalApplyRequest, tracker: AnchorTracker, context: Word.RequestContext): Promise<AnchoredRead | null> {
        const { pageOfTheRun } = request;
        /** WHEN the caller holds no page of the run CONSEQUENCE the card is reconciled in Word, as Apply finds it. */
        if (pageOfTheRun === undefined || pageOfTheRun === null) return tracker.reconcileWithin(context, "apply");
        return { rangeOoxml: pageOfTheRun, paragraphIds: [], uniqueLocalIds: [], coveredSpans: tracker.coveredSpansOver(pageOfTheRun), bookmarkRepairCount: 0, elapsedMs: 0 };
    }

    /**
     * Replaces the bookmarked paragraphs with the proposal; honours the placement the tracker's
     * last recovery chose.
     *
     * CASES
     *   - a text card over body paragraphs and table cells alike (`partsOfARedlineAcrossATable`)
     *                                     -> written part by part (`applyEachPartAcrossATable`)
     *   - otherwise                       -> written in one write (`applyOnBookmarksInOneWrite`)
     */
    async applyOnBookmarks(request: ProposalApplyRequest): Promise<AppliedProposalOutcome> {
        /** WHEN the card is markup CONSEQUENCE it names no paragraph to split by, and is written whole. */
        if (request.redline.kind !== "texts") return ProposalApplier.writtenInOneWrite(await this.applyOnBookmarksInOneWrite(request));
        const partsAcrossATable = await partsOfARedlineAcrossATable(request.redline, request.bookmarkNames);
        /** WHEN the card's paragraphs are all cells or all body CONSEQUENCE one write carries it. */
        if (partsAcrossATable === null) return ProposalApplier.writtenInOneWrite(await this.applyOnBookmarksInOneWrite(request));
        return this.applyEachPartAcrossATable(request, partsAcrossATable);
    }

    /** The outcome of a card written in one write: its one package is all it wrote. */
    private static writtenInOneWrite(outcome: ProposalOutcome): AppliedProposalOutcome {
        return { ...outcome, writtenPackageOoxmls: [outcome.packageOoxml] };
    }

    /**
     * Writes a card split at a table, part by part in order, each part as a card of its own.
     *
     * WHY each part is reconciled in Word and not built over the run's page
     *   Apply All's page of the run is the whole card's range; a body paragraph's part built
     *   over it would write the whole range into that one paragraph. And once the first part
     *   is written, that page no longer reads as the document.
     *
     * WHY every part is tried
     *   One part that cannot be written never keeps the others from the document: a cell the
     *   user removed leaves the body paragraphs to write, as one card never blocks a run.
     *
     * CASES, once every part has been tried
     *   - no part landed          -> the first part's error, as the card's: a location not found
     *                                stays one, and the card reads as one write that failed
     *   - a part wrote nothing    -> `PartialRedlineApply` over the parts that landed, the first
     *                                such error its cause. Nothing that landed is taken back.
     *   - a part wrote and reports itself partly applied (`PartialRedlineApply`, a cell the user
     *     removed, a paragraph written back)
     *                             -> that report, the first one: the card reads partly applied,
     *                                naming what it named
     *   - every part landed whole -> the card's outcome
     *
     * EXAMPLE
     *   parts [cells, "A. Supplier Services", "Customer may request…"], the cells not found
     *   -> both body parts written, then PartialRedlineApply(2, 3, location not found)
     *
     * RETURNS
     *   The last part's outcome with every part's change and paragraph counts summed, and
     *   every part's package.
     */
    private async applyEachPartAcrossATable(request: ProposalApplyRequest, parts: RedlinePartAcrossATable[]): Promise<AppliedProposalOutcome> {
        const outcomes: ProposalOutcome[] = [];
        let landedPartCount = 0;
        let firstReportOfAPartlyAppliedPart: PartialRedlineApply | null = null;
        let firstFailureOfAPartThatWroteNothing: unknown = null;
        for (const part of parts) {
            try {
                outcomes.push(await this.applyOnBookmarksInOneWrite({ bookmarkNames: part.bookmarkNames, redline: part.redline, cardId: request.cardId, pageOfTheRun: null }));
                landedPartCount += 1;
            } catch (error) {
                /** CASES: a part that wrote and reports itself partly applied landed; any other error means the part wrote nothing. The next part is tried either way. */
                if (error instanceof PartialRedlineApply) {
                    landedPartCount += 1;
                    firstReportOfAPartlyAppliedPart ??= error;
                } else {
                    firstFailureOfAPartThatWroteNothing ??= error;
                }
            }
        }
        /** WHEN no part landed CONSEQUENCE the card failed as one write would have. */
        if (landedPartCount === 0) throw firstFailureOfAPartThatWroteNothing;
        /** WHEN a part wrote nothing CONSEQUENCE the card reads partly applied over the parts that landed. */
        if (firstFailureOfAPartThatWroteNothing !== null) throw new PartialRedlineApply(landedPartCount, parts.length, ProposalApplier.errorOf(firstFailureOfAPartThatWroteNothing));
        /** WHEN a part reported itself partly applied CONSEQUENCE the card reads partly applied, naming what it named. */
        if (firstReportOfAPartlyAppliedPart !== null) throw firstReportOfAPartlyAppliedPart;
        return ProposalApplier.outcomeOfEveryPart(outcomes);
    }

    /**
     * A part's failure as the cause a `PartialRedlineApply` carries.
     *
     * CASES
     *   - an Error            -> itself
     *   - anything else thrown -> an Error naming it: Office.js can reject with a plain object
     */
    private static errorOf(failure: unknown): Error {
        /** WHEN the part threw an Error CONSEQUENCE it is the cause as thrown. */
        if (failure instanceof Error) return failure;
        return new Error(String(failure));
    }

    /**
     * One outcome for a card written in parts: the last part's package and stamp, every
     * part's changes and paragraphs counted, and every part's package in the order written.
     *
     * EXAMPLE
     *   parts changed 2, 1, 1 changes over 2, 1, 1 paragraphs -> changeCount 4, paragraphCount 4,
     *   writtenPackageOoxmls the three parts' packages
     */
    private static outcomeOfEveryPart(outcomes: ProposalOutcome[]): AppliedProposalOutcome {
        /**
         * EXAMPLE
         *   three outcomes, length 3 -> index 3 - 1 = 2, the last part written
         */
        const lastOutcome = outcomes[outcomes.length - 1];
        return {
            ...lastOutcome,
            changeCount: outcomes.reduce((changeCount, outcome) => changeCount + outcome.changeCount, 0),
            paragraphCount: outcomes.reduce((paragraphCount, outcome) => paragraphCount + outcome.paragraphCount, 0),
            writtenPackageOoxmls: outcomes.map((outcome) => outcome.packageOoxml),
        };
    }

    /** Replaces the bookmarked paragraphs with the proposal in one write. */
    private async applyOnBookmarksInOneWrite(request: ProposalApplyRequest): Promise<ProposalOutcome> {
        const proposal = new AnchoredProposal(request.redline, "asWord");
        /**
         * WHEN the redline has neither a paragraph payload nor a row or table change
         *   CONSEQUENCE
         *     throw a user-facing error: there is nothing to write
         */
        if (!proposal.hasChangesToApply) throw new UserFacingError("The redline holds no paragraph to apply.");
        const tracker = this.trackers.trackerFor(request.bookmarkNames, request.redline);
        const stampedAt = new Date().toISOString();
        return withWordContextWrite(async (context) => {
            const anchored = await ProposalApplier.anchoredReadFor(request, tracker, context);
            /**
             * WHEN the card's paragraphs are gone and it only deletes them
             *      (`redlineWritesNothingWhereItsParagraphsAreGone`)
             *   CONSEQUENCE
             *     the card is done and nothing is written: the user already made the deletion
             *     it asks for, and the tracker ran no recovery for it.
             */
            if (anchored === null && redlineWritesNothingWhereItsParagraphsAreGone(request.redline)) return ProposalApplier.nothingWrittenOutcome(stampedAt);
            const bookmarks = new OfficeDocumentBookmarks(context, this.config);
            const range = anchored ? await bookmarks.paragraphRange(request.bookmarkNames) : null;
            /**
             * WHEN reconcile did not leave both pieces needed to write
             *   CASES
             *     - !anchored  -> no live tracker position to apply at
             *     - !range     -> no bookmarked paragraph range to write into
             *   CONSEQUENCE
             *     fail as location not found
             */
            if (!anchored || !range) throw new ApplyFailureError(CANNOT_LOCATE_TARGET_MESSAGE, APPLY_FAILURE_LOCATION_NOT_FOUND);
            const placement = tracker.placement;
            const readOfTheBuild = await ProposalApplier.readOfTheBuild(request, tracker, anchored, context);
            /**
             * CASES
             *   - Apply All handed its page -> the card as handed: Apply All's fold read it over its spans already
             *   - single Apply              -> the card read over its spans on the page cut above, as the fold reads it
             */
            const proposalOverItsSpans = ProposalApplier.callerHoldsAPageOfTheRun(request) ? proposal : ProposalApplier.proposalReadOverCoveredSpans(proposal, request.redline, readOfTheBuild);
            const built = proposalOverItsSpans.build({ anchored: readOfTheBuild, placement, stampedAt });
            void RedlineRegionDisagreementReport.record({ verdict: built.outcome.regionVerdict, readPackageOoxml: readOfTheBuild.rangeOoxml, redline: request.redline, coveredSpans: readOfTheBuild.coveredSpans, where: "apply" });
            AnchorLog.record({
                kind: "apply",
                bookmarkNames: request.bookmarkNames,
                placementKind: placement.kind,
                paragraphsRead: built.textsOnPage.length,
                coveredSpans: anchored.coveredSpans,
                changeCount: built.outcome.changeCount,
                /**
                 * EXAMPLE
                 *   built.textsOnPage = ["Alpha", "Beta"]
                 *   index 0         -> "Alpha"
                 *   empty read      -> "" via ?? ""
                 */
                liveFirstText: AnchorLog.excerptStart(built.textsOnPage[0] ?? "", AnchorLog.EXCERPT_LENGTH),
                /**
                 * EXAMPLE
                 *   built.textsOnPage      = ["Alpha", "Beta", "Gamma"]
                 *   length               = 3
                 *   last index           = 3 - 1 = 2
                 *   entry at index 2     -> "Gamma"
                 *   no live paragraphs   -> "" via ?? ""
                 */
                liveLastText: AnchorLog.excerptEnd(built.textsOnPage[built.textsOnPage.length - 1] ?? "", AnchorLog.EXCERPT_LENGTH),
                /**
                 * EXAMPLE
                 *   built.placed.allChangesAcceptedTexts = ["Alpha", "Beta"]
                 *   length                     = 2
                 *   last index                 = 2 - 1 = 1
                 *   entry at index 1           -> "Beta"
                 *   no accepted text           -> "" via ?? ""
                 */
                desiredLastText: AnchorLog.excerptEnd(built.placed.allChangesAcceptedTexts[built.placed.allChangesAcceptedTexts.length - 1] ?? "", AnchorLog.EXCERPT_LENGTH),
            });
            const tableChanges = proposalOverItsSpans.tableChanges;
            /**
             * WHY the card's own texts count as well as the build
             *   Word reads the empty paragraph that ends a cell as its whole row, every cell of
             *   it, and the build over that row finds no change for a card that fills the
             *   paragraph: H, "" -> "By: ", built 0 changes over the signature row and returned
             *   here without writing (step-235552, 2026-10-16). The cell writer reads and builds
             *   each cell on its own, so a card that changes words goes on to it whatever the
             *   build over the range found.
             */
            const cardChangesParagraphs = built.outcome.changeCount > 0 || proposalOverItsSpans.changesParagraphText();
            /**
             * WHEN neither the paragraphs nor the tables change
             *   CONSEQUENCE
             *     return the outcome without writing anything
             */
            if (!cardChangesParagraphs && tableChanges === null) return built.outcome;
            /** True once the cell writer wrote the card's paragraphs, cell by cell, instead of the package. */
            let paragraphsWrittenByCellWriter = false;
            /**
             * WHEN the paragraphs change
             *   CONSEQUENCE
             *     the package replaces the bookmarked range; a card whose only changes are rows skips this write
             */
            if (cardChangesParagraphs) {
                const firstParagraphCell = range.paragraphs.getFirst().parentTableCellOrNullObject;
                firstParagraphCell.load("isNullObject");
                await context.sync();
                /**
                 * WHEN the card's paragraphs are table cells and it places no paragraph beside them
                 *   CONSEQUENCE
                 *     each cell is written on its own. Word returns a range over cells as a table
                 *     fragment, and replacing that range with the fragment nests a table inside the
                 *     first cell instead of changing the cells.
                 */
                const writesIntoCells = !firstParagraphCell.isNullObject && !relocatesBesideParagraph(placement);
                /**
                 * CASES
                 *   - cells                                      -> the cell writer, which builds each cell itself
                 *   - body paragraphs the build found changes in -> the package over the range
                 *   - body paragraphs the build found none in    -> nothing written, as before
                 */
                if (writesIntoCells) {
                    await CellChangeWriter.write(context, proposalOverItsSpans, stampedAt, bookmarks, request.cardId);
                    paragraphsWrittenByCellWriter = true;
                } else if (built.outcome.changeCount > 0) await withTemporaryTrackingMode(context, Word.ChangeTrackingMode.off, async () => {
                    /**
                     * The package goes in as Word's own export reads.
                     *
                     * CASES, for the read the card was built over
                     *   - the document's paragraphs alone -> the empty paragraph Word ends its export with is put
                     *                                        back after the last of them (`OoxmlRange.withWordsClosingParagraph`)
                     *   - Word's export as given, its
                     *     paragraphs not paired with
                     *     Word's (`wordsExportAsGiven`)   -> as built: it already ends with Word's paragraph, and a
                     *                                        second one would stay in the document as an empty line
                     */
                    const packageOverTheRange = readOfTheBuild.wordsExportAsGiven === true ? built.outcome.packageOoxml : OoxmlRange.withWordsClosingParagraph(built.outcome.packageOoxml);
                    const written = range.insertOoxml(packageOverTheRange, Word.InsertLocation.replace);
                    range.select();
                    await context.sync();
                    /**
                     * WHEN the write used a beside-the-paragraph placement
                     *   CONSEQUENCE
                     *     settle bookmarks onto the inserted paragraphs
                     *     -> the tracker moves to on_paragraph for the next read and apply
                     */
                    if (relocatesBesideParagraph(placement)) {
                        await ProposalApplier.settleOnInsertedParagraphs({
                            context,
                            tracker,
                            bookmarks,
                            written,
                            insertedCount: built.placed.insertions.length,
                            placedAfterAnchor: placement.kind === "after_paragraph",
                        });
                    }
                });
            }
            /**
             * WHEN the card carries row or table changes
             *   CONSEQUENCE
             *     they are written after the paragraphs through Word's table API under Track Changes
             *     (`TableChangeWriter`), and their count joins the outcome's.
             */
            const tableChangeCount = tableChanges === null ? 0 : (await TableChangeWriter.write(context, tableChanges)).changeCount;
            /**
             * CASES, for what the card reports once everything it asked for is written
             *   - the cell writer wrote it -> the cells it could not reach (`reportCellsNotWritten`): a
             *                                 missing cell is never written back
             *   - the package wrote it     -> the paragraphs the user deleted that it wrote back
             *                                 (`reportParagraphsWrittenBack`)
             */
            if (paragraphsWrittenByCellWriter) await ProposalApplier.reportCellsNotWritten(request.redline, bookmarks);
            else if (built.outcome.changeCount > 0) ProposalApplier.reportParagraphsWrittenBack(request.redline, built.placed.insertions, placement);
            return { ...built.outcome, changeCount: built.outcome.changeCount + tableChangeCount };
        });
    }

    /**
     * Reports a table card whose cells the cell writer could not all reach: the card is
     * partly applied, naming each changed cell by its row (`cardCellsNotWrittenOf`).
     *
     * WHEN Word no longer finds the bookmark of a cell the card changes
     *      (`OfficeDocumentBookmarks.bookmarkNamesNotFoundInDocument`): the user removed its row
     *   CONSEQUENCE
     *     `CellsNotWrittenRedlineApply`, a `PartialRedlineApply`, once the other cells are
     *     written. A one-card Apply All takes this path, and reported the engine's write-back
     *     of the missing cell, which the cell writer never writes ("“Week 6” was deleted…
     *     wrote it back", live run of A17, 2026-10-18).
     *
     * WHY Word is asked, not the tracker's read of the card's range
     *   Word exports a range that crosses rows as its first row alone. Judged by that read,
     *   every cell after the first row read as gone: a card whose three rows were all
     *   written reported two of them missing, and the run of "gone" cells paired row 2's
     *   citation with row 3's question ("Ex. 2 / OPEN: When was the work complet…", §39,
     *   -26 at a5abfd4e1, 2026-10-19). A cell the cell writer found and could not read is
     *   that writer's own refusal, never reported here.
     *
     * EXAMPLE
     *   card over the DUE cells of rows 1-3, row 3 "Acceptance testing" deleted
     *   -> CellsNotWrittenRedlineApply naming "Acceptance testing / Week 6"
     *   card over the QUESTION cells of rows 1-3, no row deleted
     *   -> nothing: the card applied as written
     */
    private static async reportCellsNotWritten(redline: RedlinePayload, bookmarks: OfficeDocumentBookmarks): Promise<void> {
        /** WHEN the card is markup CONSEQUENCE it names no cell. */
        if (redline.kind !== "texts") return;
        /**
         * The bookmark of every card paragraph that names one, the cells Word is asked about.
         *
         * CASES, per card paragraph
         *   - it names a bookmark   -> that bookmark
         *   - its bookmark is null  -> none: a paragraph the card adds has no cell to look for
         *
         * NULL
         *   A null bookmark is left out, never asked of Word.
         *
         * EXAMPLE
         *   paragraphs [_due_r1, _due_r2, null] -> ["_due_r1", "_due_r2"]
         */
        const cardCellBookmarkNames = redline.paragraphs.flatMap((cardParagraph) => (cardParagraph.bookmark === null ? [] : [cardParagraph.bookmark]));
        const bookmarkNamesGone = await bookmarks.bookmarkNamesNotFoundInDocument(cardCellBookmarkNames);
        const presence: CardCellPresence = { cellIsOnPage: (bookmarkName) => !bookmarkNamesGone.has(bookmarkName), cellIsGone: (bookmarkName) => bookmarkNamesGone.has(bookmarkName) };
        const { goneCellLabels, unreturnedCellLabels } = cardCellsNotWrittenOf(redline.paragraphs, presence);
        const missingCellCount = goneCellLabels.length + unreturnedCellLabels.length;
        /** WHEN every changed cell was reached CONSEQUENCE the card applied as written. */
        if (missingCellCount === 0) return;
        const changedCellCount = redline.paragraphs.filter((cardParagraph) => !TextEditPlanner.requestWritesNothing(cardParagraph.textWhenProposed, cardParagraph.textProposed)).length;
        /**
         * EXAMPLE
         *   changed cells 3, missing 1 -> 3 - 1 = 2 landed
         */
        throw new CellsNotWrittenRedlineApply(goneCellLabels, unreturnedCellLabels, changedCellCount - missingCellCount, changedCellCount);
    }

    /** True when Apply All handed the page its fold read the card on (`ProposalApplyRequest.pageOfTheRun`). */
    private static callerHoldsAPageOfTheRun(request: ProposalApplyRequest): boolean {
        return request.pageOfTheRun !== undefined && request.pageOfTheRun !== null;
    }

    /**
     * The read single Apply builds the card over: the card's page cut from one
     * whole-document read, the page Apply All builds the same card over
     * (`BookmarkedPages.applyPageOf`, `readPagesFromOneDocumentRead`).
     *
     * WHY
     *   Built over Word's own read of its paragraph, a card in a table cell met the whole
     *   row, the label cell beside it and the empty paragraph Word ends the export with,
     *   and the referee judged neither path intended: card #13 was not written by Apply and
     *   was written by Apply All, whose page holds the cell's paragraph alone.
     *
     * CASES
     *   - Apply All handed its page                  -> the read made over that page (`anchoredReadFor`)
     *   - the whole-document read cuts the card's page -> that page, over the spans the card's tracker
     *                                                    covers on it (`coveredSpansOver`)
     *   - it cuts none (a bookmark gone from the read,
     *     a block content control's paragraphs)       -> the reconciled read, as Word gave it
     *   - Word throws on the whole-document read      -> the reconciled read, logged, as Apply All's
     *                                                    run reads every page from Word when its
     *                                                    whole-document read throws (`documentPagesForApply`)
     *
     * EXAMPLE
     *   card #13 on the cell bookmarked _win_number_stride
     *     Word's read       the label cell, the card's cell, an empty paragraph
     *     the page cut here the card's cell alone
     */
    private static async readOfTheBuild(request: ProposalApplyRequest, tracker: AnchorTracker, anchored: AnchoredRead, context: Word.RequestContext): Promise<AnchoredRead> {
        if (ProposalApplier.callerHoldsAPageOfTheRun(request)) return anchored;
        let bodyOoxml: string;
        try {
            const bodyRead = context.document.body.getOoxml();
            await context.sync();
            bodyOoxml = bodyRead.value;
        } catch (error) {
            AnchorLog.record({ kind: "error", bookmarkNames: request.bookmarkNames, step: "read document", message: AnchorLog.messageOf(error) });
            return anchored;
        }
        const cardBookmarkNames = request.redline.kind === "texts" ? request.redline.paragraphs.flatMap((cardParagraph) => (cardParagraph.bookmark === null ? [] : [cardParagraph.bookmark])) : [];
        const listedBookmarkNames = Array.from(new Set([...request.bookmarkNames, ...cardBookmarkNames]));
        const applyPage = OoxmlRange.bookmarkedPagesOf(bodyOoxml).applyPageOf(request.bookmarkNames, listedBookmarkNames);
        /** WHEN the whole-document read cuts no page for the card CONSEQUENCE the reconciled read, as Word gave it. */
        if (applyPage === null || applyPage.kind === "readFromWord") return anchored;
        /** The cut holds the document's paragraphs alone, whatever the reconciled read held (`AnchoredRead.wordsExportAsGiven`). */
        return { ...anchored, rangeOoxml: applyPage.packageOoxml, wordsExportAsGiven: false, coveredSpans: tracker.coveredSpansOver(applyPage.packageOoxml) };
    }

    /**
     * The card single Apply builds and writes: the card read over the spans it covers in
     * this read, the reading Apply All gives every card before it builds
     * (`cardReadOverCoveredSpansOfPage`).
     *
     * CASES
     *   - a text card one of whose paragraphs reaches past its span -> a proposal over the widened card
     *   - a text card whose spans are its whole paragraphs          -> the proposal as made
     *   - a markup card                                             -> the proposal as made: it carries no texts to widen
     *
     * EXAMPLE
     *   card #13 on "Supplier's LIABILITY cap will be $1.00 USD. Redline this paragraph please with the changes by others please "
     *   span { startOffset: 0, endOffset: 43 } -> both texts widened by the 63 characters after the end marker
     */
    private static proposalReadOverCoveredSpans(proposal: AnchoredProposal, redline: RedlinePayload, anchored: AnchoredRead): AnchoredProposal {
        if (redline.kind !== "texts") return proposal;
        const cardOverSpans = cardReadOverCoveredSpansOfPage(redline, anchored.rangeOoxml, anchored.coveredSpans);
        /** WHEN no paragraph of the card was widened CONSEQUENCE the proposal as made. */
        if (cardOverSpans === redline) return proposal;
        return new AnchoredProposal(textsPayloadFrom(cardOverSpans), "asWord");
    }

    /** The outcome of a card that writes nothing: no package, no change, applied as it stands. */
    private static nothingWrittenOutcome(stampedAt: string): ProposalOutcome {
        return { packageOoxml: "", changeCount: 0, paragraphCount: 0, stampedAt, regionVerdict: null };
    }

    /**
     * Reports a card whose write put back paragraphs the user had deleted: the card is
     * partly applied, naming them, once everything it asked for is written.
     *
     * CASES, for the paragraphs the package wrote back
     *   - placed beside a paragraph (every paragraph of the card was gone, `DeletedParagraphStep`
     *     or the server)                -> each card paragraph it writes where its own is gone
     *                                      (`paragraphIsWrittenWhereGone`)
     *   - laid on its paragraphs        -> each insertion carrying a card paragraph's bookmark
     *                                      (`RedlineSource.placedOnPageParagraphs`)
     *   - none                          -> the card applied as written
     *
     * CONSEQUENCE, when one was written back
     *   `WrittenBackRedlineApply`, a `PartialRedlineApply`: the card is credited and reads
     *   partly applied, naming each paragraph by the text the user deleted, as Apply All
     *   reports the same card. Nothing written is taken back.
     *
     * EXAMPLE
     *   card [_pay, _term "The term is one year." -> "The term is two years."], _term deleted
     *   -> WrittenBackRedlineApply(["The term is one year."])
     */
    private static reportParagraphsWrittenBack(redline: RedlinePayload, insertions: ParagraphInsertion[], placement: RedlinePlacement): void {
        /** WHEN the card is markup CONSEQUENCE it names no paragraph to write back. */
        if (redline.kind !== "texts") return;
        const writtenBackBookmarkNames = insertions.flatMap((insertion) => (insertion.carriedBookmarkName === undefined || insertion.carriedBookmarkName === null ? [] : [insertion.carriedBookmarkName]));
        const writtenBackParagraphs = relocatesBesideParagraph(placement)
            ? redline.paragraphs.filter((cardParagraph) => paragraphIsWrittenWhereGone(cardParagraph.textWhenProposed, cardParagraph.textProposed))
            : redline.paragraphs.filter((cardParagraph) => cardParagraph.bookmark !== null && writtenBackBookmarkNames.includes(cardParagraph.bookmark));
        /** WHEN nothing was written back CONSEQUENCE the card applied as written. */
        if (writtenBackParagraphs.length === 0) return;
        throw new WrittenBackRedlineApply(writtenBackParagraphs.map((cardParagraph) => cardParagraph.textWhenProposed));
    }

    /**
     * After a beside-the-paragraph placement was written, the redline's
     * paragraphs are new paragraphs in the document: the bookmarks move onto
     * them and the tracker settles on `on_paragraph`, so the next read and
     * the next Apply treat them as the redline's own paragraphs.
     */
    private static async settleOnInsertedParagraphs({
        context,
        tracker,
        bookmarks,
        written,
        insertedCount,
        placedAfterAnchor,
    }: InsertedParagraphsSettlement): Promise<void> {
        const paragraphs = written.paragraphs;
        paragraphs.load("items/uniqueLocalId,items/text");
        await context.sync();
        const items = paragraphs.items;
        /**
         * CASES
         *   - placedAfterAnchor   -> take the last insertedCount paragraphs from items
         *   - otherwise           -> take the first insertedCount paragraphs from items
         *
         * EXAMPLE
         *   items.length    = 5
         *   insertedCount   = 2
         *   after anchor    -> slice(3)    -> items[3], items[4]
         *   before anchor   -> slice(0, 2) -> items[0], items[1]
         */
        const inserted = placedAfterAnchor ? items.slice(Math.max(0, items.length - insertedCount)) : items.slice(0, insertedCount);
        /**
         * WHEN no inserted paragraphs were found in the written range
         *   CONSEQUENCE
         *     return without moving bookmarks onto an empty target
         */
        if (inserted.length === 0) return;
        await bookmarks.layOver(tracker.bookmarkNames, inserted.map((paragraph) => ({ uniqueLocalId: paragraph.uniqueLocalId, text: paragraph.text })));
        tracker.settleOnParagraph();
        AnchorLog.record({ kind: "settled_on_inserted", bookmarkNames: tracker.bookmarkNames, insertedCount: inserted.length, placedAfterAnchor });
    }

    /** Inserts the redline's paragraphs whole after the bookmarked range, the paragraph-add form of a remedy. */
    async insertAfterBookmarks(request: ProposalApplyRequest): Promise<ProposalOutcome> {
        const proposal = new AnchoredProposal(request.redline, "asWord");
        /**
         * WHEN the redline has no paragraph payload
         *   CONSEQUENCE
         *     throw a user-facing error: this remedy inserts paragraphs, so at least one is required
         */
        if (!proposal.hasParagraphs) throw new UserFacingError("The redline holds no paragraph to apply.");
        const tracker = this.trackers.trackerFor(request.bookmarkNames, request.redline);
        const stampedAt = new Date().toISOString();
        return withWordContextWrite(async (context) => {
            const anchored = await tracker.reconcileWithin(context, "apply");
            const bookmarks = new OfficeDocumentBookmarks(context, this.config);
            const range = anchored ? await bookmarks.paragraphRange(request.bookmarkNames) : null;
            const trackerFoundAnchor = Boolean(anchored);
            const bookmarksFoundParagraphRange = trackerFoundAnchor && Boolean(range);
            const canInsertAfterRecoveredLocation = trackerFoundAnchor && bookmarksFoundParagraphRange;
            /**
             * WHEN reconcile did not leave both pieces needed to insert
             *   CASES
             *     - !anchored  -> no live tracker position to insert from
             *     - !range     -> no bookmarked paragraph range to insert after
             *   CONSEQUENCE
             *     fail as location not found
             */
            if (!canInsertAfterRecoveredLocation) throw new ApplyFailureError(CANNOT_LOCATE_TARGET_MESSAGE, APPLY_FAILURE_LOCATION_NOT_FOUND);
            const packageOoxml = proposal.insertedParagraphsPackage(stampedAt);
            await withTemporaryTrackingMode(context, Word.ChangeTrackingMode.off, async () => {
                const target = range.paragraphs.getLast().insertParagraph("", Word.InsertLocation.after).getRange();
                target.insertText(" ", Word.InsertLocation.start);
                await context.sync();
                target.insertOoxml(packageOoxml, Word.InsertLocation.replace);
                await context.sync();
            });
            const paragraphCount = proposal.textsProposed.length;
            return { packageOoxml, changeCount: paragraphCount, paragraphCount, stampedAt, regionVerdict: null };
        });
    }
}
